import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { handLineAfter } from '../handLine';
import { itemFade } from '../motionReveal';
import { GallerySection } from './GalleryPage';

const experience = [
  {
    name: 'Accenture Song',
    detail: 'Product and conversation designer',
    time: '2026 - Now',
    href: 'https://www.accenture.com/en/about/accenture-song-index',
    external: true,
    logo: '/logos/accenture-song.jpg',
  },
  {
    name: 'Wayfair',
    detail: 'Product designer',
    time: '2025 – 2026',
    href: '/case-studies/wayfair-ai-assistant',
    logo: '/logos/wayfair.svg',
  },
  {
    name: 'Freebites',
    detail: 'Founding product designer & cofounder',
    time: '2023 – 2026',
    href: '/case-studies/freebites',
    logo: '/logos/freebites.svg',
  },
  {
    name: 'Wayfair Professional',
    detail: 'Product design intern',
    time: '2024',
    href: '/case-studies/wayfair-professional',
    logo: '/logos/wayfair_professional.svg',
  },
  {
    name: 'Massachusetts National Guard',
    detail: 'Product designer',
    time: '2025',
    href: '/case-studies/massachusetts-national-guard',
    logo: '/logos/massnationalguard.jpeg',
  },
  { 
    name: 'Keep Massachusetts Beautiful',
    detail: 'Product designer',
    time: '2023 - 2024',
    href: '/case-studies/keep-massachusetts-beautiful',
    logo: '/logos/keepmass.jpeg',
  }
];

const Page = styled.main`
  flex: 1 0 auto;
  background: var(--bg);
  color: var(--text);
  overflow-x: clip;
`;

const Content = styled(motion.div)`
  max-width: 46rem;
  margin: 0 auto;
  padding: 5rem 2rem 5.5rem;

  @media (max-width: 640px) {
    padding: 3rem 1.25rem 4rem;
  }
`;

const wideSection = css`
  width: min(64rem, calc(100vw - 4rem));
  margin-left: calc((100% - min(64rem, 100vw - 4rem)) / 2);
`;

const Intro = styled.div`
  ${wideSection}
  display: grid;
  grid-template-columns: minmax(0, 28rem) minmax(0, 1fr);
  gap: 2.25rem;
  align-items: stretch;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.75rem;
  }
`;

const Copy = styled.div`
  min-width: 0;
  padding-top: 0.35rem;
`;

const Hello = styled.h1`
  margin: 0 0 16px;
  font-family: var(--font-serif);
  font-weight: 500;
  font-size: 24px;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--text);
`;

const Paragraph = styled.p`
  margin: 0 0 1rem;
  max-width: 28rem;
  font-family: var(--font-sans);
  font-size: 18px;
  font-weight: 400;
  line-height: 1.55;
  letter-spacing: -0.01em;
  color: var(--text-muted);

  a {
    position: relative;
    color: inherit;
    text-decoration: none;
  }

  a::after {
    ${handLineAfter}
  }

  a:hover {
    color: var(--text);
  }

  a:hover::after {
    opacity: 1;
    transform: scaleY(1.2);
  }
`;

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.85rem;
  margin-top: 1.35rem;
  color: var(--text-muted);
`;

const TextLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 400;
  letter-spacing: -0.01em;
  color: inherit;
  text-decoration: none;

  &:hover {
    color: var(--text);
  }

  &:focus-visible {
    outline: 2px solid var(--text);
    outline-offset: 3px;
    border-radius: 2px;
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const LinkDivider = styled.span`
  width: 1px;
  height: 16px;
  background: currentColor;
  opacity: 0.45;
`;

const IntroPlaceholder = styled.div`
  width: 100%;
  min-height: 11rem;
  border-radius: 12px;
  background: var(--surface);

  @media (max-width: 960px) {
    aspect-ratio: 16 / 9;
  }
`;

const Section = styled(motion.section)`
  ${wideSection}
  margin-top: 64px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 16px;
  font-family: var(--font-serif);
  font-weight: 500;
  font-size: 24px;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--text);
`;

const experienceRow = css`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  column-gap: 0.7rem;
  align-items: center;
  padding: 0.85rem 0;
  color: inherit;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid var(--text);
    outline-offset: 4px;
  }

  @media (max-width: 640px) {
    grid-template-columns: auto minmax(0, 1fr);
  }
`;

const ExpLink = styled(Link)`
  ${experienceRow}
`;

const ExpAnchor = styled.a`
  ${experienceRow}
`;

const ExpLogoWrap = styled.span`
  grid-column: 1;
  grid-row: 1 / span 2;
  align-self: stretch;
  justify-self: start;
  position: relative;
  aspect-ratio: 1;
  min-height: 0;
  border-radius: 25%;
  overflow: hidden;

  @media (max-width: 640px) {
    width: 40px;
    height: 40px;
    align-self: start;
    grid-row: 1 / span 3;
    margin-top: 0.1rem;
  }
`;

const ExpLogo = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ExpTime = styled.span`
  grid-column: 3;
  grid-row: 1;
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 400;
  letter-spacing: -0.01em;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  @media (max-width: 640px) {
    grid-column: 2;
    grid-row: 3;
    margin-top: 0.15rem;
  }
`;

const QuestList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const QuestName = styled.span`
  position: relative;
  display: block;
  width: fit-content;
  max-width: 100%;
  grid-column: 2;
  grid-row: 1;
  font-family: var(--font-sans);
  font-size: 18px;
  font-weight: 500;
  letter-spacing: -0.02em;
  color: var(--text);

  &::after {
    ${handLineAfter}
    opacity: 0;
  }

  a:hover &::after {
    opacity: 1;
    transform: scaleY(1.2);
  }
`;

const QuestDetail = styled.span`
  display: block;
  grid-column: 2;
  grid-row: 2;
  margin-top: 0.15rem;
  font-family: var(--font-sans);
  font-size: 16px;
  font-weight: 400;
  letter-spacing: -0.01em;
  color: var(--text-muted);
`;

const ExperienceRow = ({ item }) => {
  const inner = (
    <>
      <ExpLogoWrap>
        <ExpLogo src={item.logo} alt="" />
      </ExpLogoWrap>
      <QuestName>{item.name}</QuestName>
      <ExpTime>{item.time}</ExpTime>
      <QuestDetail>{item.detail}</QuestDetail>
    </>
  );

  if (item.external) {
    return (
      <li>
        <ExpAnchor href={item.href} target="_blank" rel="noopener noreferrer">
          {inner}
        </ExpAnchor>
      </li>
    );
  }

  return (
    <li>
      <ExpLink to={item.href}>{inner}</ExpLink>
    </li>
  );
};

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.725-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M4.98 3.5C4.98 4.88 3.88 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8.5h4V24h-4V8.5zM8.5 8.5h3.8v2.1h.05c.53-1 1.84-2.1 3.79-2.1 4.05 0 4.8 2.67 4.8 6.14V24h-4v-7.7c0-1.84-.03-4.2-2.56-4.2-2.56 0-2.95 2-2.95 4.06V24h-4V8.5z" />
  </svg>
);

const ResumeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7z" />
    <path d="M14 3v4h4" />
    <path d="M8 13h8M8 17h5" />
  </svg>
);

const AboutPage = () => {
  useEffect(() => {
    const sample = (label) => {
      const main = document.querySelector('main');
      const content = main?.firstElementChild;
      const wide = content?.querySelector('div');
      const mainCs = main ? getComputedStyle(main) : null;
      const contentCs = content ? getComputedStyle(content) : null;
      const htmlCs = getComputedStyle(document.documentElement);
      const bodyCs = getComputedStyle(document.body);
      const scrollers = [];
      const nodes = [document.documentElement, document.body, ...document.querySelectorAll('body *')];
      nodes.forEach((el) => {
        const oy = getComputedStyle(el).overflowY;
        const ox = getComputedStyle(el).overflowX;
        const extraY = el.scrollHeight - el.clientHeight;
        const extraX = el.scrollWidth - el.clientWidth;
        if (extraY > 2 || extraX > 2) {
          scrollers.push({
            tag: el.tagName,
            oy,
            ox,
            extraY,
            extraX,
          });
        }
      });
      // #region agent log
      fetch('http://127.0.0.1:7723/ingest/d7b880e8-87ce-4390-95ff-712f6f14be97',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'4acb28'},body:JSON.stringify({sessionId:'4acb28',runId:'post-fix',hypothesisId:'A-E',location:'AboutPage.js:sample',message:'overflow sample',data:{label,scrollY:window.scrollY,mainCount:document.querySelectorAll('main').length,html:{sh:document.documentElement.scrollHeight,ch:document.documentElement.clientHeight,sw:document.documentElement.scrollWidth,cw:document.documentElement.clientWidth,oy:htmlCs.overflowY,ox:htmlCs.overflowX,gutter:htmlCs.scrollbarGutter},body:{sh:document.body.scrollHeight,ch:document.body.clientHeight,sw:document.body.scrollWidth,cw:document.body.clientWidth,oy:bodyCs.overflowY,ox:bodyCs.overflowX},main:main?{sh:main.scrollHeight,ch:main.clientHeight,sw:main.scrollWidth,cw:main.clientWidth,oy:mainCs.overflowY,ox:mainCs.overflowX,h:mainCs.height}:null,content:content?{transform:contentCs.transform,sh:content.scrollHeight,ch:content.clientHeight,sw:content.scrollWidth,cw:content.clientWidth}:null,wide:wide?{sw:wide.scrollWidth,cw:wide.clientWidth,w:getComputedStyle(wide).width}:null,scrollerCount:scrollers.length,scrollers:scrollers.slice(0,6)},timestamp:Date.now()})}).catch(()=>{});
      // #endregion
    };
    const times = [0, 80, 300, 600, 1200, 2200];
    const ids = times.map((t) => setTimeout(() => sample(String(t)), t));
    return () => ids.forEach(clearTimeout);
  }, []);

  return (
  <Page>
    <Content
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
    >
      <Intro>
        <Copy>
          <Hello>Hello!</Hello>
          <Paragraph>
            I’m from Queens, New York, and I live in Boston. I design at{' '}
            <a href="https://www.accenture.com/en/about/accenture-song-index" target="_blank" rel="noopener noreferrer">
              Accenture Song
            </a>
            , working on product and conversation design. Before that I designed at{' '}
            <a href="https://www.wayfair.com/" target="_blank" rel="noopener noreferrer">
              Wayfair
            </a>{' '}
            and cofounded{' '}
            <a href="https://www.freebites.com/" target="_blank" rel="noopener noreferrer">
              Freebites
            </a>
            .
          </Paragraph>
          <Paragraph>
            I’m drawn to complicated problems, the ones where the gap between how something is supposed to work and how people actually use it is widest. In my free time I build what I design.
          </Paragraph>
          <Links>
            <TextLink href="https://x.com/sarahjun1" target="_blank" rel="noopener noreferrer" aria-label="X">
              <XIcon />
            </TextLink>
            <TextLink href="https://www.linkedin.com/in/sarahjun1" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <LinkedInIcon />
            </TextLink>
            <LinkDivider aria-hidden="true" />
            <TextLink
              href="https://drive.google.com/file/d/1z588d93q2fjpBHJLzhQTf2NY6_GOGuKr/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ResumeIcon />
              Resume
            </TextLink>
          </Links>
        </Copy>
        <IntroPlaceholder aria-hidden="true" />
      </Intro>

      <Section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={itemFade}
      >
        <SectionTitle>Experience</SectionTitle>
        <QuestList>
          {experience.map((item) => (
            <ExperienceRow key={item.name} item={item} />
          ))}
        </QuestList>
      </Section>

      <Section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.15 }}
        variants={itemFade}
      >
        <GallerySection />
      </Section>
    </Content>
  </Page>
  );
};

export default AboutPage;
